import {
  Box,
  Button,
  Cards,
  KeyValuePairs,
  Link,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import ReactJson from "react-json-view";
import { formatCurrencyValue } from "../../helpers/cbeSummaryUtils";
import createFlareConfigLink from "../../helpers/flare-config-utils";
import {
  createFlashUIMatcherLink,
  createRaceLink,
} from "../../helpers/linkUtils";
import { CanonicalBusinessEvent, CBERecord } from "../../types";
import { useReactJsonProps } from "../../hooks/useReactJsonProps";
import { Stage } from "../../lusca-search-api/generated-src";
import { useMatchSets } from "../../contexes/MatchSetsContext";
import { extractMatchKeyFromRecord } from "../../helpers/matchKeyUtils";
import { MatchSetInfo } from "../../types/health";
import { MatchSetState } from "../../types/matchSetState";

interface RecordCardProps {
  item: CBERecord;
  cbe: CanonicalBusinessEvent;
  stage: Stage;
  marketplaceId: string;
}

interface FieldProps {
  label: React.ReactNode;
  value: React.ReactNode;
  link?: string;
}

function renderAmount(item: CBERecord): React.ReactElement {
  const amount = parseFloat(
    item.content?.functionalAmount?.value ?? item.functionalAmount?.value ?? "0"
  );
  const entryType = item.content?.entryType ?? item.entryType ?? "UNKNOWN";
  const currencyCode =
    item.content?.functionalAmount?.currencyCode ??
    item.functionalAmount?.currencyCode ??
    "";

  const isDebit =
    (entryType === "DEBIT" && amount > 0) ||
    (entryType === "CREDIT" && amount < 0);

  return (
    <Box color={isDebit ? "text-status-error" : "text-status-success"}>
      {formatCurrencyValue(Math.abs(amount), currencyCode)} ({entryType})
    </Box>
  );
}

function Field({ label, value, link }: FieldProps) {
  if (value === undefined) return null;

  return (
    <div>
      <strong>{label}</strong>
      <br />
      {link ? (
        <Link href={link} external>
          {value}
        </Link>
      ) : (
        value
      )}
    </div>
  );
}

function MainInfoSection({ item }: { item: CBERecord }) {
  return (
    <div style={{ height: "150px" }}>
      <SpaceBetween direction="vertical" size="s">
        <Field label="Amount" value={renderAmount(item)} />
        <Field
          label="Financial Component"
          value={
            item.content?.financialComponent ??
            item.flareConfigFinancialComponent
          }
        />
        <Field
          label="Financial Component Group"
          value={
            item.content?.financialComponentGroup ??
            item.flareConfigFinancialComponentGroup
          }
        />
      </SpaceBetween>
    </div>
  );
}

function MatchSetTooltip({
  ms,
  matchKey,
  marketplaceId,
  stage,
}: {
  ms: MatchSetInfo;
  matchKey: string;
  marketplaceId: string;
  stage: Stage;
}) {
  if (ms.state === MatchSetState.PENDING) {
    return (
      <Box color="text-body-secondary">
        Match set not yet registered. Check back later.
      </Box>
    );
  }
  const cur = ms.amount?.currencyCode || "";
  return (
    <SpaceBetween size="xs">
      <KeyValuePairs
        columns={2}
        items={[
          ...(ms.amount
            ? [
                {
                  label: "Debits",
                  value: (
                    <Box color="text-status-error">
                      {ms.numDebits ?? 0}× {ms.amount.debitValue ?? "0"} {cur}
                    </Box>
                  ),
                },
                {
                  label: "Credits",
                  value: (
                    <Box color="text-status-success">
                      {ms.numCredits ?? 0}× {ms.amount.creditValue ?? "0"} {cur}
                    </Box>
                  ),
                },
                {
                  label: "Net",
                  value: (
                    <Box fontWeight="bold">
                      {ms.amount.value} {cur}
                    </Box>
                  ),
                },
              ]
            : []),
          ...(ms.accountNumber
            ? [{ label: "Account", value: ms.accountNumber }]
            : []),
          ...(ms.accountingDate
            ? [{ label: "Date", value: ms.accountingDate.split(" ")[0] }]
            : []),
        ]}
      />
      <Link
        href={createFlashUIMatcherLink(
          marketplaceId,
          matchKey,
          stage,
          ms.accountNumber
        )}
        external
        fontSize="body-s"
      >
        View in Matcher
      </Link>
    </SpaceBetween>
  );
}

function AdditionalInfoSection({
  item,
  marketplaceId,
  stage,
}: {
  item: CBERecord;
  marketplaceId: string;
  stage: Stage;
}) {
  const { matchSets } = useMatchSets();

  const deferralId =
    item.clientAttributes?.deferral_id?.stringValue ??
    item.content?.clientAttributes?.deferral_id?.stringValue;

  const matchKeyValue = extractMatchKeyFromRecord(item);

  const rcslEntrySourceId =
    item.clientAttributes?.rcsl_entry_source_id?.stringValue ??
    item.content?.clientAttributes?.rcsl_entry_source_id?.stringValue;

  const matchKeyLabel = rcslEntrySourceId
    ? "RCSL Entry Source Id"
    : "Invoice Pipeline Source Id";

  // For RCSL/invoice keys, prefer the CLOSED match set (the actual RCSL match set)
  const matchSetForRecord = matchKeyValue
    ? matchSets.find(
        (ms) =>
          ms.matchKey === matchKeyValue && ms.state === MatchSetState.CLOSED
      ) ??
      matchSets.find((ms) => ms.matchKey === matchKeyValue) ??
      null
    : null;

  // Find deferral-specific match set: a different match set for the deferral_id
  // When deferral_id === RCSL key, multiple match sets exist for the same key value;
  // pick the one not already shown by the RCSL badge
  const deferralMatchSet = deferralId
    ? matchSets.find(
        (ms) =>
          ms.matchKey === deferralId &&
          ms !== matchSetForRecord &&
          ms.state !== MatchSetState.CLOSED
      ) ??
      matchSets.find(
        (ms) => ms.matchKey === deferralId && ms !== matchSetForRecord
      ) ??
      null
    : null;
  const showDeferralMatchSet = !!deferralMatchSet;

  return (
    <div style={{ height: "120px" }}>
      <SpaceBetween direction="vertical" size="s">
        <Field
          label="Account"
          value={item.content?.accountType ?? item.accountType}
        />
        {matchKeyValue && (
          <Field
            label={
              <>
                {matchKeyLabel}
                {matchSetForRecord && (
                  <>
                    {" · "}
                    <Popover
                      size="medium"
                      position="top"
                      dismissButton={false}
                      triggerType="text"
                      content={
                        <MatchSetTooltip
                          ms={matchSetForRecord}
                          matchKey={matchKeyValue}
                          marketplaceId={marketplaceId}
                          stage={stage}
                        />
                      }
                    >
                      <Box
                        color={
                          matchSetForRecord.state === MatchSetState.CLOSED
                            ? "text-status-success"
                            : matchSetForRecord.state === MatchSetState.PENDING
                            ? "text-status-inactive"
                            : "text-status-warning"
                        }
                        display="inline"
                      >
                        {matchSetForRecord.state === MatchSetState.PARTIAL
                          ? MatchSetState.OPEN
                          : matchSetForRecord.state}
                      </Box>
                    </Popover>
                  </>
                )}
              </>
            }
            value={
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  wordBreak: "break-all",
                }}
                title={matchKeyValue}
              >
                <Link
                  href={createFlashUIMatcherLink(
                    marketplaceId,
                    matchKeyValue,
                    stage,
                    matchSetForRecord?.accountNumber
                  )}
                  external
                >
                  {matchKeyValue}
                </Link>
              </div>
            }
          />
        )}
        {deferralId && (
          <Field
            label={
              <>
                Deferral Id
                {showDeferralMatchSet && (
                  <>
                    {" · "}
                    <Popover
                      size="medium"
                      position="top"
                      dismissButton={false}
                      triggerType="text"
                      content={
                        <MatchSetTooltip
                          ms={deferralMatchSet}
                          matchKey={deferralId}
                          marketplaceId={marketplaceId}
                          stage={stage}
                        />
                      }
                    >
                      <Box
                        color={
                          deferralMatchSet.state === MatchSetState.CLOSED
                            ? "text-status-success"
                            : deferralMatchSet.state === MatchSetState.PENDING
                            ? "text-status-inactive"
                            : "text-status-warning"
                        }
                        display="inline"
                      >
                        {deferralMatchSet.state === MatchSetState.PARTIAL
                          ? MatchSetState.OPEN
                          : deferralMatchSet.state}
                      </Box>
                    </Popover>
                  </>
                )}
              </>
            }
            value={deferralId}
            link={createRaceLink(marketplaceId, deferralId, stage)}
          />
        )}
        <Field
          label="Tax Jurisdiction Name"
          value={
            item.clientAttributes?.tax_jurisdiction_name?.stringValue ??
            item.content?.clientAttributes?.tax_jurisdiction_name?.stringValue
          }
        />
      </SpaceBetween>
    </div>
  );
}

function ActionsSection({
  item,
  cbe,
  stage,
}: {
  item: CBERecord;
  cbe: CanonicalBusinessEvent;
  stage: Stage;
}) {
  const jsonViewProps = useReactJsonProps({ sortKeys: true });
  return (
    <SpaceBetween direction="horizontal" size="xxxs">
      <Popover
        position="top"
        size="large"
        triggerType="custom"
        content={
          <Box padding="s">
            <ReactJson src={item} {...jsonViewProps} />
          </Box>
        }
      >
        <Button iconName="command-prompt" variant="link">
          Show JSON
        </Button>
      </Popover>
      <Button
        variant="link"
        href={createFlareConfigLink(cbe, item, stage)}
        target="_blank"
        rel="noopener noreferrer"
        iconAlign="right"
        iconName="external"
      >
        Flare Config
      </Button>
    </SpaceBetween>
  );
}

export function RecordCard({
  item,
  cbe,
  stage,
  marketplaceId,
}: RecordCardProps) {
  // Section renderers use closure over props — not actual component re-creation
  const sections = [
    {
      id: "mainInfo",
      content: (record: CBERecord) => <MainInfoSection item={record} />,
    },
    {
      id: "additionalInfo",
      content: (record: CBERecord) => (
        <AdditionalInfoSection
          item={record}
          marketplaceId={marketplaceId}
          stage={stage}
        />
      ),
    },
    {
      id: "actions",
      content: (record: CBERecord) => (
        <Box margin={{ top: "l" }}>
          <ActionsSection item={record} cbe={cbe} stage={stage} />
        </Box>
      ),
    },
  ];

  return (
    <Cards
      items={[item]}
      cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
      cardDefinition={{
        header: () =>
          item["@type"] ?? item.flareConfigFinancialComponent ?? "Unknown",
        sections,
      }}
    />
  );
}
